import { useState } from "react";
import { ShieldAlert, Search } from "lucide-react";
import { motion } from "framer-motion";

export default function CVESearch({ onSubmit, compact = false, loading = false }) {
  const [value, setValue] = useState("");

  const handle = (e) => {
    e.preventDefault();
    const v = value.trim().toUpperCase();
    if (!v) return;
    onSubmit(v);
  };

  if (compact) {
    return (
      <form onSubmit={handle} className="flex items-center gap-2 w-full">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            data-testid="cve-search-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="CVE-YYYY-NNNNN"
            disabled={loading}
            className="w-full bg-transparent border border-slate-300 rounded-md pl-9 pr-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200 disabled:opacity-50"
          />
        </div>
        <button
          type="submit"
          data-testid="cve-search-submit"
          disabled={loading}
          className="shrink-0 bg-slate-900 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Analyze
        </button>
      </form>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="w-full max-w-4xl mx-auto"
    >
      <div className="flex items-center gap-2 mb-6">
        <ShieldAlert className="h-4 w-4 text-violet-700" />
        <span className="overline">CVE Threat Analytics // v1.0</span>
      </div>

      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900 mb-5">
        Understand any vulnerability.
        <br />
        <span className="text-violet-700">From CVE-ID to pseudo-exploit</span> in
        seconds.
      </h1>

      <p className="text-slate-600 text-base md:text-lg max-w-2xl mb-10 leading-relaxed">
        Paste a CVE identifier. We pull NVD, MITRE, GitHub Security Advisories,
        and the actual patch diff - then our AI walks through the
        root cause, a <span className="text-slate-900">sanitized</span>{" "}
        pseudo-exploit, and how hard it really is to weaponize.
      </p>

      <form onSubmit={handle} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
          <input
            data-testid="cve-search-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="CVE-2024-3094"
            autoFocus
            disabled={loading}
            className="w-full bg-white border border-slate-200 rounded-md pl-12 pr-4 py-4 font-mono text-base md:text-lg text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200 disabled:opacity-50"
          />
        </div>
        <button
          type="submit"
          data-testid="cve-search-submit"
          disabled={loading}
          className="bg-slate-900 text-white px-7 py-4 rounded-md font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Analyzing…" : "Run Analysis"}
        </button>
      </form>

      <div className="mt-6 flex flex-wrap gap-2 text-xs">
        <span className="overline mr-2">Try:</span>
        {["CVE-2021-44228", "CVE-2024-3094", "CVE-2023-23397", "CVE-2022-22965"].map(
          (s) => (
            <button
              key={s}
              onClick={() => onSubmit(s)}
              data-testid={`example-${s}`}
              className="font-mono text-slate-600 hover:text-violet-700 border border-slate-200 hover:border-violet-300 rounded-md px-2.5 py-1 transition-colors"
            >
              {s}
            </button>
          )
        )}
      </div>
    </motion.div>
  );
}
